import { AxiosError } from 'axios'
import { useCallback } from 'react'
import {
  createSearchParams,
  useNavigate,
  useSearchParams,
} from 'react-router-dom'
import { toast } from 'sonner'

import {
  DEFAULT_PAGE_SIZE,
  DEFAULT_REGION_BANK_HEADER_VALUE,
  INITIAL_PAGE_SIZE,
} from '@/constants'
import { useFeedStore } from '@/stores/feed/feedStore'
import {
  getPriceSegmentsClientKey,
  getSelectedPriceSegmentCodes,
} from '@/stores/feed/priceSegmentsStore'
import { useSearchStore } from '@/stores/feed/searchStore'
import { useUserInfoStore } from '@/stores/user/userInfoStore'
import type {
  ApiErrorDetail,
  ApiErrorResponseData,
  CharacteristicFilterWithRange,
  EntityFilterType,
  FeedProductInterface,
  SearchNomenclatureRequestBody,
  SearchResponseData,
  StoreShortWithSupplierType,
} from '@/types'
import api from '@/utils/api/api'
import getGeneralErrorMessage from '@/utils/api/errors/getGeneralErrorMessage'
import { unwrapSearchResult } from '@/utils/api/unwrapSearchResult'
import {
  cleanCharacteristics,
  cleanFilterItems,
  cleanProductsContent,
  cleanTechnicalSuffix,
} from '@/utils/catalog/cleanDescription'
import { mergeCartPresence } from '@/utils/products/cartPresence'
import feedProductsFormatter from '@/utils/products/feedProductsFormatter'
import { hasUniformEligibleSlottedFilter } from '@/utils/search/characteristics/slottedCharacteristics'
import {
  buildCharacteristicFilterBody,
  buildStoresRequestPart,
  omitSearchRequestDefaults,
} from '@/utils/search/request/buildSearchRequestBody'
import getSearchRequestParamsFromStores from '@/utils/search/request/getSearchRequestParamsFromStores'
import { focusProductSearchInput } from '@/utils/search/ui/focusProductSearchInput'

export interface UseSearchParams {
  query: string
  categories: EntityFilterType[]
  productCodes?: number[]
  brands: EntityFilterType[]
  page: number
  characteristicFilters: CharacteristicFilterWithRange[]
  withLeftovers: boolean
  matchUniformSlottedCharacteristics?: boolean
  triggerNavigation?: boolean
  clientId?: string | null
  resetFilters?: boolean
  smartReset?: boolean
  stores?: StoreShortWithSupplierType[]
  isNextPage?: boolean
  isInitial?: boolean
  silent?: boolean
}

export default function useSearch() {
  const {
    setProducts,
    setProductsLoading,
    setPagination,
    setExpandedRows,
    setPaginatedTableLoading,
    setTotalElements,
  } = useFeedStore((state) => state)

  const { organisation, currentClient } = useUserInfoStore((state) => state)

  const {
    currentlySearchableStores,
    currentlySearchableCategories,
    currentlySearchableProductCodes,
    setSearchTerm,
    setWithLeftovers,
    setCurrentlySearchableCategories,
    setCurrentlySearchableBrands,
    setCurrentlySearchableStores,
    setCurrentlySearchableCharacteristics,
    setCurrentlySearchableProductCodes,
    setAvailableFilters,
    setSearchError,
    setServerChosenFilters,
    setInitialFilters,
  } = useSearchStore((state) => state)

  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()

  return useCallback(
    async (params: UseSearchParams): Promise<void> => {
      const {
        query,
        categories,
        brands,
        productCodes,
        page,
        characteristicFilters,
        withLeftovers,
        matchUniformSlottedCharacteristics = useSearchStore.getState()
          .matchUniformSlottedCharacteristics,
        triggerNavigation = true,
        clientId,
        resetFilters = false,
        smartReset = false,
        stores,
        isNextPage = false,
        isInitial = false,
        silent = false,
      } = params

      const trimmedQuery = query.trim().replace(/\s+/g, ' ')
      const hasTextQuery = trimmedQuery.length > 0
      const effectiveProductCodes =
        productCodes !== undefined
          ? productCodes
          : currentlySearchableProductCodes

      if (isInitial) {
        setInitialFilters({
          searchTerm: trimmedQuery,
          categories: categories,
          brands: brands,
          productCodes: effectiveProductCodes,
          parents: [],
        })
      }

      if (!silent) {
        if (!isNextPage) {
          setProductsLoading(true)
          setProducts([])
          setExpandedRows([])
          setTotalElements(0)
        }

        setPaginatedTableLoading(true)

        if (!isNextPage) {
          setPagination({
            current: INITIAL_PAGE_SIZE / DEFAULT_PAGE_SIZE,
            pageSize: DEFAULT_PAGE_SIZE,
          })
        } else {
          setPagination({
            ...useFeedStore.getState().pagination,
            current: page + 1,
          })
        }
      }

      const effectiveStores =
        stores !== undefined ? stores : currentlySearchableStores

      if (resetFilters) {
        setCurrentlySearchableCategories(categories)
        setCurrentlySearchableBrands(brands)
        setCurrentlySearchableStores(stores !== undefined ? stores : [])
        setCurrentlySearchableCharacteristics([])
        setCurrentlySearchableProductCodes([])
        setWithLeftovers(false)
        setSearchTerm(trimmedQuery)
        setServerChosenFilters(null)
        setSearchError(null)
      } else if (smartReset) {
        const resetCategories = hasTextQuery
          ? []
          : currentlySearchableCategories.length > 0
            ? currentlySearchableCategories
            : categories

        setCurrentlySearchableCategories(resetCategories)
        setCurrentlySearchableBrands([])
        setCurrentlySearchableStores([])
        setCurrentlySearchableCharacteristics([])
        setWithLeftovers(false)
        setSearchTerm(trimmedQuery)
      } else {
        setSearchTerm(trimmedQuery)
        setCurrentlySearchableCategories(categories)
        setCurrentlySearchableBrands(brands)
        setWithLeftovers(withLeftovers)
        setCurrentlySearchableCharacteristics(characteristicFilters)
        if (stores !== undefined) setCurrentlySearchableStores(stores)
      }

      if (Object.keys(organisation).length && !organisation.isLegal) {
        setSearchError('Ошибка: организация не активирована')
        setProductsLoading(false)
        setProducts([])
        return
      }

      setCurrentlySearchableProductCodes(effectiveProductCodes)

      const effectiveClientId =
        clientId !== undefined ? clientId : currentClient?.id

      const shouldMatchUniformSlotted =
        matchUniformSlottedCharacteristics &&
        !resetFilters &&
        !smartReset &&
        hasUniformEligibleSlottedFilter(characteristicFilters)

      useSearchStore
        .getState()
        .setMatchUniformSlottedCharacteristics(shouldMatchUniformSlotted)

      // Записка §10: бэкенд убрал биндинг `filter[...]` из GET-запросов,
      // поэтому сам запрос уходит POST-ом с JSON-телом. GET и URL ниже
      // остаются как были — для адресной строки и обратной совместимости.
      const requestBody: SearchNomenclatureRequestBody = {
        query: trimmedQuery,
        productCodes: effectiveProductCodes,
        ...(effectiveClientId && { client: effectiveClientId }),
        page: page,
        pageSize: isNextPage ? DEFAULT_PAGE_SIZE : INITIAL_PAGE_SIZE,
        withLeftovers: resetFilters || smartReset ? false : withLeftovers,
        withDiscontinued: false,
        exactMatch: false,
        matchUniformSlottedCharacteristics: shouldMatchUniformSlotted,
        categoryIds: [] as string[],
        brandIds: [] as string[],
        parentIds: [] as string[],
        ...buildCharacteristicFilterBody([]),
        priceSegmentCodes: getSelectedPriceSegmentCodes(
          getPriceSegmentsClientKey(effectiveClientId)
        ),
      }

      if (!smartReset) {
        requestBody.categoryIds = categories.map((c) => c.id)
        requestBody.brandIds = brands.map((b) => b.id)

        Object.assign(requestBody, buildStoresRequestPart(effectiveStores))

        if (characteristicFilters.length > 0) {
          Object.assign(
            requestBody,
            buildCharacteristicFilterBody(characteristicFilters)
          )
        }
      } else if (!hasTextQuery && categories.length > 0) {
        requestBody.categoryIds = categories.map((c) => c.id)
      }

      if (triggerNavigation) {
        const urlParams: Record<string, string | string[]> = {
          ...(trimmedQuery && { query: trimmedQuery }),
          ...(effectiveProductCodes?.length && {
            productCodes: effectiveProductCodes.join(','),
          }),
          withLeftovers: String(
            resetFilters || smartReset ? false : withLeftovers
          ),
          page: String(page),
          ...(shouldMatchUniformSlotted && {
            matchUniformSlottedCharacteristics: 'true',
          }),
        }

        if (!smartReset) {
          if (categories.length)
            urlParams.categoryIds = categories.map((c) => c.id).join(',')
          if (brands.length)
            urlParams.brandIds = brands.map((b) => b.id).join(',')

          if (effectiveStores.length > 0) {
            Object.assign(
              urlParams,
              getSearchRequestParamsFromStores(effectiveStores)
            )
          }

          characteristicFilters.forEach((filter) => {
            if (filter.type === 'NUMERIC' && filter.chosenRange) {
              if (filter.chosenRange.min != null)
                urlParams[`ranges[${filter.code}].min`] = String(
                  filter.chosenRange.min
                )
              if (filter.chosenRange.max != null)
                urlParams[`ranges[${filter.code}].max`] = String(
                  filter.chosenRange.max
                )
            }

            const values = [
              ...(filter.options || []).map((o) => String(o.value)),
              ...(filter.chosenOptions || []),
            ]
            if (values.length > 0) {
              urlParams[`filter[${filter.code}]`] = values
            }
          })
        }

        const nextSearchString = createSearchParams(urlParams).toString()
        if (nextSearchString !== searchParams.toString()) {
          const isOnFeedPage = window.location.pathname.includes('/feed')
          if (isOnFeedPage) {
            setSearchParams(urlParams)
            return
          } else {
            navigate({ pathname: '/feed', search: nextSearchString })
            return
          }
        }
      }

      try {
        const response = await api.post<
          SearchResponseData<FeedProductInterface>
        >(`/nomenclatures/search`, omitSearchRequestDefaults(requestBody), {
          headers: { 'Region-Bank': DEFAULT_REGION_BANK_HEADER_VALUE },
        })

        const result = unwrapSearchResult(response.data)
        const { chosenFilters, pagedProducts, availableFilters } = result

        const cleanedChosen = {
          ...chosenFilters,
          categories: cleanFilterItems(chosenFilters?.categories),
          brands: cleanFilterItems(chosenFilters?.brands),
          characteristics: cleanCharacteristics(chosenFilters?.characteristics),
          parents: cleanFilterItems(chosenFilters?.parents),
          stores: chosenFilters?.stores || {
            include: [],
            exclude: [],
            priority: [],
          },
        }

        if (
          effectiveProductCodes &&
          effectiveProductCodes.length > 0 &&
          (pagedProducts.content?.length ?? 0) > 0
        ) {
          const firstProduct = pagedProducts.content?.[0]
          const cat = firstProduct?.nomenclatureDto?.categoryDto
          if (
            cat &&
            (!cleanedChosen.categories || cleanedChosen.categories.length === 0)
          ) {
            cleanedChosen.categories = [
              {
                id: cat.id,
                description: cleanTechnicalSuffix(cat.description),
                count: 0,
              },
            ]
          }
        }

        setServerChosenFilters(cleanedChosen)
        setCurrentlySearchableCategories(cleanedChosen.categories || [])
        setCurrentlySearchableBrands(cleanedChosen.brands || [])
        setCurrentlySearchableCharacteristics(
          cleanedChosen.characteristics || []
        )

        if (cleanedChosen.stores?.include) {
          const flatStores = cleanedChosen.stores.include.flatMap((supp) =>
            supp.stores.map((s) => ({
              ...s,
              supplierId: supp.supplierId,
              description: s.description || 'Склад',
            }))
          )

          const uniqueStoresMap = new Map<string, StoreShortWithSupplierType>()
          flatStores.forEach((s) => {
            const key = s.id || `${s.supplierId}-${s.code}`
            if (!uniqueStoresMap.has(key)) {
              uniqueStoresMap.set(key, s)
            }
          })

          setCurrentlySearchableStores(Array.from(uniqueStoresMap.values()))
        }

        if (availableFilters) {
          setAvailableFilters({
            ...availableFilters,
            categories: cleanFilterItems(availableFilters.categories),
            brands: cleanFilterItems(availableFilters.brands),
            characteristics: cleanCharacteristics(
              availableFilters.characteristics
            ),
            parents: cleanFilterItems(availableFilters.parents),
          })
        }

        setTotalElements(pagedProducts.totalElements || 0)

        const newFormattedProducts = feedProductsFormatter(
          cleanProductsContent(pagedProducts.content || [])
        )

        if (isNextPage) {
          setProducts([
            ...useFeedStore.getState().products,
            ...newFormattedProducts,
          ])
        } else {
          setProducts(newFormattedProducts)
        }
        useFeedStore
          .getState()
          .setProductInfoToCartPresence(
            mergeCartPresence(
              useFeedStore.getState().productInfoToCartPresence,
              newFormattedProducts
            )
          )

        setPagination({
          current: isNextPage
            ? page + 1
            : INITIAL_PAGE_SIZE / DEFAULT_PAGE_SIZE,
          pageSize: DEFAULT_PAGE_SIZE,
          total: pagedProducts.totalElements || 0,
        })
        setSearchError('')
        if (!isNextPage && !silent) focusProductSearchInput()
      } catch (error) {
        if (silent) {
          toast.error('Не удалось обновить цены', {
            description: 'Попробуйте обновить страницу',
          })
          return
        }

        let errorMessage = getGeneralErrorMessage(error)
        let errorDetails: ApiErrorDetail[] = []
        let traceId: string | undefined

        if (error instanceof AxiosError) {
          traceId = (error.response?.data as ApiErrorResponseData | undefined)
            ?.traceId

          if (!error.response) {
            if (error.code === 'ERR_NETWORK') {
              errorMessage =
                'Ошибка доступа к внешнему сервису. Обратитесь к администратору.'
            } else if (error.code === 'ECONNABORTED') {
              errorMessage =
                'Превышено время ожидания ответа. Попробуйте еще раз.'
            }
          } else if (error.response.data) {
            const data = error.response.data as ApiErrorResponseData
            errorMessage = data.message || errorMessage
            errorDetails = data.details || []
          }
        }

        setSearchError({
          message: errorMessage,
          details: errorDetails,
          traceId: traceId,
        })
        setProducts([])
      } finally {
        if (!silent) {
          setProductsLoading(false)
          setPaginatedTableLoading(false)
        }
      }
    },
    [organisation, currentClient, searchParams, navigate, setInitialFilters]
  )
}
